/**
 * WorkspaceWriteService.js
 * 
 * Service for writing workspace data to Firestore.
 */

// Firebase
import { functions, httpsCallable } from 'firebaseConfig';

const callable = (name) => httpsCallable(functions, name);

/**
 * Creates a new workspace in Firestore.
 * 
 * @param {string} userKey - The user key associated with the workspace.
 * @param {string} title - The title of the workspace.
 * @param {boolean} [isDefault=false] - Whether the workspace is the default workspace for the user.
 * @param {Object|null} [ownerProfile=null] - Optional owner identity details.
 * 
 * @returns {Promise<Object|null>} The created workspace data or null if userKey is not provided.
 */
export async function createWorkspace(userKey, title, isDefault = false, ownerProfile = null) {

  // Validate userKey
  if (!userKey) return null;

  const fn = callable('createWorkspace');
  const { data } = await fn({
    title,
    isDefault,
    ownerProfile,
  });

  return data || null;
}

/**
 * Updates an existing workspace in Firestore.
 * 
 * @param {string} workspaceKey - The key of the workspace to update.
 * @param {Object} data - The data to update the workspace with.
 * 
 * @returns {Promise<void>}
 */
export async function updateWorkspace(workspaceKey, data) {

  // Validate inputs
  if (!workspaceKey || !data) return;

  const payload = {
    ...data,
  };

  // Never allow callers to overwrite owner identity from client writes
  if (Object.prototype.hasOwnProperty.call(payload, 'ownerKey')) {
    delete payload.ownerKey;
  }

  const fn = callable('updateWorkspace');
  await fn({ workspaceKey, data: payload });
}

/**
 * Updates the current space plan selection.
 *
 * @param {object} input - Update input.
 * @param {string} input.workspaceKey - Workspace key.
 * @param {string} input.planKey - Selected plan key.
 * @param {string} input.planTitle - Selected plan title.
 *
 * @returns {Promise<void>}
 */
export async function updateWorkspacePlanSelection({
  workspaceKey,
  planKey,
  planTitle,
}) {
  const cleanedWorkspaceKey = String(workspaceKey || '').trim();
  const cleanedPlanKey = String(planKey || '').trim();
  const cleanedPlanTitle = String(planTitle || '').trim();

  if (!cleanedWorkspaceKey) {
    throw new Error('workspaceKey is required');
  }

  if (cleanedPlanKey && !cleanedPlanTitle) {
    throw new Error('planTitle is required when planKey is set');
  }

  await updateWorkspace(cleanedWorkspaceKey, {
    planKey: cleanedPlanKey || null,
    planTitle: cleanedPlanKey ? cleanedPlanTitle : null,
  });
}

/**
 * Deletes a space by its key.
 * 
 * @param {string} workspaceKey - The key of the space to delete.
 * 
 * @returns {Promise<Object|null>} Operation metadata for the queued space delete.
 */
export async function deleteWorkspaceByKey(workspaceKey) {

  // Validate input
  if (!workspaceKey) return;

  const fn = callable('deleteWorkspace');
  const { data } = await fn({ workspaceKey });
  return data || null;
}
